import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import { AbstractControl, NonNullableFormBuilder, ValidatorFn, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Subscription, timer } from "rxjs";
import { take } from "rxjs/operators";
import { SnackbarService } from "../../../../shared/components/snackbar/snackbar.service";
import { ModalMaterialComponent } from "../../../../shared/components/modal/modal-material.component";
import { RecordingTableData } from "../../recording-library.component";
import { RecordingService } from "@onsip/common/services/api/resources/recording/recording.service";
import { ApiPromiseSubject } from "@onsip/common/services/api/api-promise-state.service";
import { Recording } from "@onsip/common/services/api/resources/recording/recording";
import { RecordingTypesEnum } from "@onsip/web/features/shared/components/togglableRecorder/config/recording-types.enum";
import { TogglableRecorderComponent } from "@onsip/web/features/shared/components/togglableRecorder/togglable-recorder.component";

@Component({
  selector: "onsip-recording-shelf-edit",
  templateUrl: "./recording-shelf-edit.component.html",
  styleUrls: [
    "./recording-shelf-edit.component.scss",
    "../recording-shelf-container.component.scss"
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class RecordingShelfEditComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() recording!: RecordingTableData;
  @Output() viewPanel = new EventEmitter<void>();
  @ViewChild(TogglableRecorderComponent) recorderRef!: TogglableRecorderComponent;

  private existingRecordings: Array<Recording> = [];
  loadingStateComplete$ = new BehaviorSubject(true);

  formGroup = this.fb.group({
    name: this.fb.control("", [
      Validators.required,
      Validators.maxLength(30),
      Validators.pattern("^[0-9A-Za-z #'.-]+$"),
      this.repeatNameValidator()
    ]),
    record: this.fb.control(<File | undefined>undefined, [Validators.required])
  });

  greetingType = RecordingTypesEnum.RECORD;

  private unsubscriber = new Subscription();

  constructor(
    private fb: NonNullableFormBuilder,
    private dialog: MatDialog,
    private translate: TranslateService,
    private snackbar: SnackbarService,
    private recordingService: RecordingService
  ) {}

  ngOnInit() {
    this.formGroup.patchValue({
      name: this.recording.name,
      record: new File([], this.recording.filePath)
    });

    this.unsubscriber.add(
      this.recordingService.orgState.subscribe(
        existingRecordings => (this.existingRecordings = existingRecordings)
      )
    );
  }

  ngAfterViewInit(): void {
    if (this.recording.filePath) {
      this.recorderRef.recordingsForm.controls.upload.patchValue(
        new File([], this.recording.filePath)
      );
    }

    // to trigger change detection in audio recorder
    this.unsubscriber.add(
      timer(0).subscribe(() => {
        this.recorderRef.recordingUrlsByType$.next({
          ...this.recorderRef.recordingUrlsByType$.value,
          record: this.recording.recordingUrl,
          upload: this.recording.recordingUrl
        });
      })
    );
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  togglePanel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(res => {
          if (res && res.doPrimaryAction) this.viewPanel.emit();
        })
    );
  }

  onSubmit(): void {
    if (!this.formGroup.value.record) {
      this.snackbar.openSnackBar("Recording cannot be empty.", "error");
      return;
    }

    if (this.formGroup.valid) {
      const newName = this.formGroup.controls.name.value;
      this.loadingStateComplete$.next(false);
      if (newName !== this.recording.name) {
        this.recordingService
          .recordingEdit({
            RecordingId: this.recording.recordingId,
            Name: newName,
            Number: this.recording.number
          })
          .then(result => {
            if (result.status === "success") {
              if (this.isNewRecording()) {
                this.handleRecordUpload();
              } else {
                this.snackbar.openSnackBar(
                  this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
                  "success"
                );
                this.viewPanel.emit();
              }
            } else {
              this.snackbar.openSnackBar(result.data.message, "error");
              this.recordingService.clearErrors();
            }
            this.loadingStateComplete$.next(true);
          });
      } else {
        if (this.isNewRecording()) {
          this.handleRecordUpload();
        } else {
          this.viewPanel.emit();
        }
      }
    }
  }

  private handleRecordUpload(): void {
    this.recordingService
      .recordingUpload(this.recording.recordingId, this.formGroup.controls.record.value)
      .then(result => this.handleUploadResult(result));
  }

  private handleUploadResult(result: ApiPromiseSubject<Recording>): void {
    if (result.status === "success") {
      const updatedRecording = Object.values(result.data)[0];
      const hasRecordingFile = updatedRecording.haveRecording;
      if (!hasRecordingFile && this.formGroup.value.record) {
        this.snackbar.openSnackBar(
          this.translate.instant("ONSIP_I18N.THERE_WAS_AN_ERROR_SAVING_YOUR_INFORMATION"),
          "error"
        );
      } else {
        this.snackbar.openSnackBar(
          this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
          "success"
        );
        this.viewPanel.emit();
      }
    } else {
      this.snackbar.openSnackBar(result.data.message, "error");
      this.recordingService.clearErrors();
    }
    this.loadingStateComplete$.next(true);
  }

  getNameError(): string {
    const control = this.formGroup.controls.name;
    const value = "Name";
    return control.hasError("required")
      ? this.translate.instant("ONSIP_I18N._VALUE__IS_REQUIRED", { value })
      : control.hasError("maxlength")
      ? this.translate.instant("ONSIP_I18N.VALUE_IS_LONGER_THAN_LENGTH_CHARACTERS", {
          value,
          length: control.errors?.maxlength.requiredLength
        })
      : control.hasError("pattern")
      ? this.translate.instant("ONSIP_I18N.ONLY_LETTERS_NUMBERS_OR_THE_SYMBOLS__ACCEPTED")
      : control.hasError("repeatName")
      ? this.translate.instant("ONSIP_I18N.THE_FILE_NAME_ALREADY_EXISTS", {
          name: control?.value
        })
      : "";
  }

  private isNewRecording(): boolean {
    return this.formGroup.value.record?.name !== this.recording.filePath;
  }

  private repeatNameValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const fileName: string = control.value || "";

      return this.existingRecordings
        .filter(({ recordingId }) => recordingId !== this.recording.recordingId)
        .some(({ name }) => name === fileName)
        ? { repeatName: true }
        : // eslint-disable-next-line no-null/no-null
          null;
    };
  }
}
